import React, { useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Tooltip, TooltipProps, Typography, styled } from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-premium';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchNetworkAreaHealth, updateNetworkAreaHealthApiParams } from '../../state/features/areaHealthSlice';
import { NetworkArea } from '../../interfaces/services/areas';
import { ApiStatus, Grade } from '../../utils/enums';
import { formatBalance, formatNumber, formatNumberPercent } from '../../utils/formattingUtils';
import { buildAreaLink } from '../../utils/navigationHelpers';
import { getGradeColor } from '../../utils/styleUtils';
import { formatHealthFilters } from '../../utils/stateHelpers';
import { useFilters } from '../Filters/useFilters';

const classesPrefix = 'areaHealthTable';

const classes = {
    table: `${classesPrefix}-table`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledDataGridPremium = styled(DataGridPremium)(({ theme }) => {
    return {
        [`&.${classes.table}`]: {
            '& .areaHealthTable.gradeA': {
                color: getGradeColor(Grade.A, theme),
            },
            '& .areaHealthTable.gradeB': {
                color: getGradeColor(Grade.B, theme),
            },
            '& .areaHealthTable.gradeC': {
                color: getGradeColor(Grade.C, theme),
            },
            '& .areaHealthTable.gradeD': {
                color: getGradeColor(Grade.D, theme),
            },
            '& .areaHealthTable.gradeE': {
                color: getGradeColor(Grade.E, theme),
            },
        },
    };
}) as typeof DataGridPremium;

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

const AreaHealthTable = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const filters = useFilters();

    const networkAreaHealthApiStatus = useAppSelector((state) => { return state.areaHealth.networkAreaHealthApiStatus; });
    const networkAreaHealthApiParams = useAppSelector((state) => { return state.areaHealth.networkAreaHealthApiParams; });
    const networkAreaHealth = useAppSelector((state) => { return state.areaHealth.networkAreaHealth; });

    useEffect(() => {
        dispatch(fetchNetworkAreaHealth(networkAreaHealthApiParams));
    }, [dispatch, networkAreaHealthApiParams]);

    const healthFilters = formatHealthFilters();

    const columns = useMemo((): GridColDef<NetworkArea>[] => {
        return [
            {
                headerName: 'Area',
                field: 'service_area_name',
                flex: 1,
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip title={row.service_area_name}>
                            <Link
                                component={RouterLink}
                                to={buildAreaLink(row.service_area_id, {
                                    ...filters,
                                    earliest_delivery_departure_timestamp: healthFilters.earliest_delivery_departure_timestamp,
                                    latest_delivery_departure_timestamp: healthFilters.latest_delivery_departure_timestamp,
                                })}
                            >
                                {row.service_area_name}
                            </Link>
                        </Tooltip>
                    );
                },
            },
            {
                headerName: 'Grade',
                description: 'Area Grade',
                field: 'service_area_health_grade',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span>The </span>
                                    <span className={classes.title}>Overall Grade</span>
                                    <span> for an area is an average of the Balance, Density, and Empty Miles grades.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                cellClassName: (params): string => {
                    return `areaHealthTable grade${params.row.service_area_health_grade}`;
                },
            },
            {
                headerName: 'Balance',
                field: 'average_daily_balance_amount',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>Balance</span>
                                        <span> is the difference between an area&apos;s count of daily inbound and outbound orders and assumes zero is ideal.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        Grades are based on the absolute value of the average balance of all areas in the network:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = Less than 2</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>C</span>
                                            <span> = Between 2 and 5</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Greater than 5</span>
                                        </li>
                                    </ul>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The map displays the top 5 and bottom 5 areas by average daily balance.
                                    </Typography>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatBalance(value, 2, 2);
                },
                cellClassName: (params): string => {
                    return `areaHealthTable grade${params.row.average_daily_balance_grade}`;
                },
            },
            {
                headerName: 'Density',
                field: 'average_daily_density_amount',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>Density</span>
                                        <span> is the count of daily inbound orders to an area.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The grade thresholds for density are dynamically calculated based on the operating data set&apos;s maximum daily density for the past 30 days:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = 75% or more of the maximum daily density</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>B</span>
                                            <span> = 50% to 75% of the maximum daily density</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>D</span>
                                            <span> = 25% to 50% of the maximum daily density</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Less than 25% of the maximum daily density or an average daily density less than 2</span>
                                        </li>
                                    </ul>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The map displays the top 5 and bottom 5 areas by average daily density.
                                    </Typography>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: (params): string => {
                    return `areaHealthTable grade${params.row.average_daily_density_grade}`;
                },
            },
            {
                headerName: 'Empty Mile %',
                field: 'average_empty_miles_percentage_amount',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <div>
                                    <Typography variant='body2' gutterBottom className={classes.helpText}>
                                        <span className={classes.title}>Empty Mile %</span>
                                        <span> is the quotient of order empty miles and order total miles expressed as a percentage.</span>
                                    </Typography>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The grade thresholds for Empty Miles are based on the percent of empty miles:
                                    </Typography>
                                    <ul>
                                        <li>
                                            <span className={classes.title}>A</span>
                                            <span> = Less than 5%</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>C</span>
                                            <span> = Between 5% and 9%</span>
                                        </li>
                                        <li>
                                            <span className={classes.title}>E</span>
                                            <span> = Greater than 9%</span>
                                        </li>
                                    </ul>
                                    <Typography variant='body2' className={classes.helpText}>
                                        The map displays the bottom 5 outbound lanes of the 5 areas with the greatest Empty Mile %.
                                    </Typography>
                                </div>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumberPercent(value, 2, 2);
                },
                cellClassName: (params): string => {
                    return `areaHealthTable grade${params.row.average_empty_miles_percentage_grade}`;
                },
            },
        ];
    }, [filters, healthFilters.earliest_delivery_departure_timestamp, healthFilters.latest_delivery_departure_timestamp]);

    const handlePaginationModelChange = (model: GridPaginationModel): void => {
        dispatch(updateNetworkAreaHealthApiParams({
            ...networkAreaHealthApiParams,
            // MUI has 0 based paging, and our API is 1 based, so we need to add a 1 here.
            pageNumber: model.page + 1,
            pageSize: model.pageSize,
        }));
    };

    const handleSortModelChange = (newSortModel: GridSortModel): void => {
        dispatch(updateNetworkAreaHealthApiParams({
            ...networkAreaHealthApiParams,
            sortBy: newSortModel,
        }));
    };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    return (
        <StyledDataGridPremium
            classes={{
                root: classes.table,
            }}
            rows={networkAreaHealth.areas}
            columns={columns}
            density='compact'
            disableColumnFilter
            disableRowSelectionOnClick
            disableRowGrouping
            disableAggregation
            loading={networkAreaHealthApiStatus === ApiStatus.Idle || networkAreaHealthApiStatus === ApiStatus.Loading}
            getRowId={(row): string => { return row.service_area_id; }}
            rowCount={networkAreaHealth.areas_count}
            pagination
            paginationMode='server'
            paginationModel={{
                // MUI has 0 based paging, and our API is 1 based, so we need to subtract a 1 here.
                page: networkAreaHealthApiParams.pageNumber - 1,
                pageSize: networkAreaHealthApiParams.pageSize,
            }}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            sortingMode='server'
            sortModel={networkAreaHealthApiParams.sortBy}
            onSortModelChange={handleSortModelChange}
            slotProps={{
                columnsManagement: {
                    searchPredicate: columnSearchPredicate,
                },
            }}
        />
    );
};

export default AreaHealthTable;
